import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { Button, CircularProgress, Typography } from '@material-ui/core';
import axios from 'axios';
import { ethers } from 'ethers';

// This page only ever needs to call back to the host that served it -- there
// is no legitimate case where /pay should hit a different API host -- so it
// deliberately does not use the app's configurable SERVER_NAME constant (which
// depends on a REACT_APP_SERVER_NAME build-time env var some deploys omit).
const API_BASE = '/api';

const ERC20_ABI = [
  'function transfer(address to, uint256 amount) returns (bool)',
  'function decimals() view returns (uint8)',
];

// Used only if a wallet doesn't already recognize the target chain (wallet_switchEthereumChain
// throws code 4902). Ethereum mainnet is known to every wallet by default, so in practice this
// only ever fires for Base -- kept per-chain rather than hardcoded so it stays correct if a
// future payment method targets a chain the user's wallet hasn't seen before.
const CHAIN_ADD_PARAMS = {
  base: {
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    rpcUrls: ['https://mainnet.base.org'],
    blockExplorerUrls: ['https://basescan.org'],
  },
  ethereum: {
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    rpcUrls: ['https://ethereum.publicnode.com'],
    blockExplorerUrls: ['https://etherscan.io'],
  },
};

const useStyles = makeStyles(() => ({
  root: {
    fontFamily: "'Inter', sans-serif",
    color: '#ffffff',
    backgroundColor: 'rgba(5,8,20,0.98)',
    minHeight: '100vh',
    padding: 24,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
  },
  card: {
    width: '100%',
    maxWidth: 480,
    borderRadius: 14,
    border: '1px solid rgba(77, 121, 255, 0.35)',
    background: 'radial-gradient(circle at 18% 18%, rgba(77,121,255,0.22), rgba(10,14,30,0.98))',
    boxShadow: '0 0 24px rgba(77,121,255,0.14)',
    padding: 24,
    boxSizing: 'border-box',
  },
  title: {
    fontSize: 20,
    fontWeight: 900,
    marginBottom: 12,
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 8,
    fontSize: 14,
    color: 'rgba(255,255,255,0.85)',
  },
  status: {
    marginTop: 16,
    fontSize: 14,
    color: 'rgba(255,255,255,0.7)',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  error: {
    marginTop: 16,
    fontSize: 14,
    color: '#ff6b6b',
  },
  debug: {
    marginTop: 16,
    padding: 12,
    fontSize: 11,
    lineHeight: 1.5,
    color: 'rgba(255,255,255,0.6)',
    background: 'rgba(0,0,0,0.4)',
    borderRadius: 8,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    userSelect: 'text',
  },
  button: {
    marginTop: 20,
    width: '100%',
  },
}));

function useQueryParam(name) {
  const location = useLocation();
  return new URLSearchParams(location.search).get(name);
}

export default function PayPage() {
  const classes = useStyles();
  const sessionId = useQueryParam('session');

  const [session, setSession] = useState(null);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('loading');

  const [debugInfo, setDebugInfo] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setError('Missing "session" parameter in the URL.');
      setStatus('error');
      return undefined;
    }

    let cancelled = false;
    const requestUrl = `${API_BASE}/payments/credits/session/${encodeURIComponent(sessionId)}`;
    axios.get(requestUrl)
      .then((res) => {
        if (cancelled) return;
        setSession(res.data);
        setStatus('ready');
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.error || 'This payment link is invalid or has expired.');
        setDebugInfo({
          requestUrl,
          httpStatus: err?.response?.status ?? null,
          responseBody: err?.response?.data ?? null,
          errorMessage: err?.message ?? null,
          errorCode: err?.code ?? null,
          isNetworkError: !err?.response,
        });
        setStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const handlePay = useCallback(async () => {
    if (!session) return;
    if (!window.ethereum) {
      setError('No wallet extension detected. Install MetaMask (or a compatible wallet) and reload this page.');
      setStatus('error');
      return;
    }

    try {
      setError(null);
      setStatus('connecting');

      // "any" tells ethers not to lock onto the network detected at
      // construction time -- without it, switching MetaMask to Base a few
      // lines below throws NETWORK_ERROR ("underlying network changed") on
      // the next provider call, since ethers v5 otherwise treats a changed
      // network as fatal.
      const provider = new ethers.providers.Web3Provider(window.ethereum, 'any');
      const [account] = await provider.send('eth_requestAccounts', []);

      if (String(account).toLowerCase() !== String(session.wallet).toLowerCase()) {
        setError(`This payment is for wallet ${session.wallet}, but your connected wallet is ${account}. Switch accounts in your wallet extension and reload this page.`);
        setStatus('error');
        return;
      }

      const targetChainId = Number(session.quote.payment.chainId);
      const targetChainIdHex = `0x${targetChainId.toString(16)}`;
      const network = await provider.getNetwork();

      if (Number(network.chainId) !== targetChainId) {
        try {
          await window.ethereum.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: targetChainIdHex }],
          });
        } catch (switchError) {
          if (switchError && switchError.code === 4902) {
            const addParams = CHAIN_ADD_PARAMS[session.quote.payment.chain] || CHAIN_ADD_PARAMS.base;
            await window.ethereum.request({
              method: 'wallet_addEthereumChain',
              params: [{
                chainId: targetChainIdHex,
                chainName: session.quote.payment.network || session.quote.payment.chain,
                ...addParams,
              }],
            });
          } else {
            throw switchError;
          }
        }
      }

      setStatus('paying');
      const signer = provider.getSigner();
      const tokenContract = new ethers.Contract(session.quote.payment.tokenAddress, ERC20_ABI, signer);

      let decimals = 18;
      try {
        decimals = await tokenContract.decimals();
      } catch (decimalsError) {
        // Fall back to the ERC20-standard 18 decimals if the read fails.
      }

      const amount = ethers.utils.parseUnits(session.quote.payment.requiredAmount, decimals);
      const tx = await tokenContract.transfer(session.quote.payment.treasuryAddress, amount);

      setStatus('waiting-confirmation');
      await tx.wait();

      setStatus('reporting');
      await axios.post(`${API_BASE}/payments/credits/session/${encodeURIComponent(sessionId)}/complete`, {
        txHash: tx.hash,
      });

      setStatus('done');
    } catch (err) {
      setError(err?.response?.data?.error || err?.reason || err?.message || 'Payment failed. Please try again.');
      setDebugInfo({
        httpStatus: err?.response?.status ?? null,
        responseBody: err?.response?.data ?? null,
        errorMessage: err?.message ?? null,
        errorCode: err?.code ?? null,
        isNetworkError: err && !err.response && !err.reason,
      });
      setStatus('error');
    }
  }, [session, sessionId]);

  const statusMessages = {
    connecting: 'Connecting to your wallet...',
    paying: 'Waiting for you to approve the transfer in your wallet...',
    'waiting-confirmation': 'Transaction submitted — waiting for confirmation on-chain...',
    reporting: 'Payment confirmed — finishing up...',
  };

  return (
    <div className={classes.root}>
      <div className={classes.card}>
        <Typography className={classes.title}>e3d netdoctor — pay for report</Typography>

        {status === 'loading' && (
          <div className={classes.status}>
            <CircularProgress size={16} style={{ color: '#ffffff' }} />
            <span>Loading payment details...</span>
          </div>
        )}

        {session && (status === 'ready' || status === 'connecting' || status === 'paying' || status === 'waiting-confirmation' || status === 'reporting') && (
          <>
            <div className={classes.row}><span>Wallet</span><span>{session.wallet}</span></div>
            <div className={classes.row}><span>Amount</span><span>{session.quote.payment.requiredAmount} {session.quote.payment.token}</span></div>
            <div className={classes.row}><span>Network</span><span>{session.quote.payment.network}</span></div>
            <div className={classes.row}><span>Credits</span><span>{session.quote.requestedIssuedCredits}</span></div>
          </>
        )}

        {status === 'ready' && (
          <Button
            className={classes.button}
            variant="contained"
            color="primary"
            onClick={handlePay}
          >
            Connect wallet & pay
          </Button>
        )}

        {statusMessages[status] && (
          <div className={classes.status}>
            <CircularProgress size={16} style={{ color: '#ffffff' }} />
            <span>{statusMessages[status]}</span>
          </div>
        )}

        {status === 'done' && (
          <Typography className={classes.status}>
            Payment received — you can close this tab and return to your terminal.
          </Typography>
        )}

        {error && <Typography className={classes.error}>{error}</Typography>}

        {debugInfo && (
          <pre className={classes.debug}>{JSON.stringify(debugInfo, null, 2)}</pre>
        )}
      </div>
    </div>
  );
}
